/** Custom React Flow nodes: one visual per workflow node type. */
import { memo, type ReactNode } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Icono, type NombreIcono } from '@xiltrion/ui/icono';
import { NODE_META, nodeSubtitle, type DNode } from './model';
import { useDesigner } from './store';
import { useLookups } from './lookups';

function useNodeIssues(id: string) {
  return useDesigner((s) => { let level: 'error' | 'warning' | null = null; for (const i of s.issues) { if (i.nodeId !== id) continue; if (i.level === 'error') return 'error'; level = 'warning'; } return level; });
}
function IssueBadge({ level }: { level: 'error' | 'warning' | null }) {
  if (!level) return null;
  const err = level === 'error';
  return <span className={`absolute -top-2 -right-2 w-5 h-5 rounded-full text-xs inline-flex items-center justify-center shadow ${err ? 'bg-destructive text-destructive-foreground' : 'bg-warning text-warning-foreground'}`} title={err ? 'Tiene errores' : 'Tiene advertencias'} aria-label={err ? 'Tiene errores' : 'Tiene advertencias'}><Icono nombre={err ? 'mdi:exclamation-thick' : 'mdi:alert'} /></span>;
}
const handleStyle = { width: 10, height: 10, background: 'var(--card)', border: '2px solid var(--muted-foreground)' };

function Shell({ id, type, selected, children, shape = 'card', width = 190 }: { id: string; type: DNode['type']; selected?: boolean; children: ReactNode; shape?: 'card' | 'circle' | 'diamond' | 'bar' | 'pill'; width?: number }) {
  const meta = NODE_META[type];
  const level = useNodeIssues(id);
  const ring = selected ? '0 0 0 3px var(--ring)' : level === 'error' ? '0 0 0 2px var(--destructive)' : 'var(--shadow-xs)';
  const base = { background: meta.bg, borderColor: meta.border, color: meta.color, boxShadow: ring } as const;
  if (shape === 'circle') return (
    <div className="relative" style={{ width: 84 }}>
      {type !== 'start' && <Handle type="target" position={Position.Left} style={handleStyle} />}
      <div className={`rounded-full border-2 h-[84px] w-[84px] flex flex-col items-center justify-center text-center ${type === 'end' || type === 'handoff' ? 'outline outline-2 outline-offset-2' : ''}`} style={{ ...base, outlineColor: meta.border }}>{children}</div>
      {type !== 'end' && type !== 'handoff' && <Handle type="source" position={Position.Right} style={handleStyle} />}
      <IssueBadge level={level} />
    </div>
  );
  if (shape === 'diamond') return (
    <div className="relative" style={{ width: 200, height: 100 }}>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div className="absolute inset-0 border-2" style={{ ...base, clipPath: 'polygon(50% 0, 100% 50%, 50% 100%, 0 50%)', borderRadius: 6 }} />
      <div className="absolute inset-0 flex items-center justify-center text-center px-10">{children}</div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
      <IssueBadge level={level} />
    </div>
  );
  if (shape === 'bar') return (
    <div className="relative" style={{ width: 150 }}>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div className="border-2 rounded-md px-3 py-1.5 text-center" style={{ ...base, borderLeftWidth: 8, borderRightWidth: 8 }}>{children}</div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
      <IssueBadge level={level} />
    </div>
  );
  if (shape === 'pill') return (
    <div className="relative" style={{ width: 160 }}>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div className="border-2 rounded-full px-3 py-2 text-center" style={base}>{children}</div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
      <IssueBadge level={level} />
    </div>
  );
  return (
    <div className="relative" style={{ width }}>
      <Handle type="target" position={Position.Left} style={handleStyle} />
      <div className="border-2 rounded-lg px-3 py-2 bg-card" style={{ ...base, background: 'var(--card)' }}>{children}</div>
      <Handle type="source" position={Position.Right} style={handleStyle} />
      <IssueBadge level={level} />
    </div>
  );
}

const Title = ({ icon, name, sub, color }: { icon: NombreIcono; name: string; sub?: string; color: string }) => (
  <div className="flex items-start gap-2 min-w-0">
    <span className="w-7 h-7 rounded-md inline-flex items-center justify-center text-lg shrink-0" style={{ background: 'rgba(0,0,0,0.06)', color }} aria-hidden><Icono nombre={icon} /></span>
    <span className="min-w-0"><span className="block font-medium text-foreground text-[13px] leading-tight truncate" title={name}>{name}</span>{sub && <span className="block text-[11px] text-muted-foreground leading-tight mt-0.5 truncate" title={sub}>{sub}</span>}</span>
  </div>
);

export const nodeTypes = {
  start: memo(({ id, data, selected }: NodeProps<DNode>) => (
    <Shell id={id} type="start" selected={selected} shape="circle"><Icono nombre={NODE_META.start.icon} className="text-2xl" aria-hidden /><span className="text-xs font-medium leading-tight px-1 truncate w-full">{data.wf.name}</span></Shell>
  )),
  end: memo(({ id, data, selected }: NodeProps<DNode>) => (
    <Shell id={id} type="end" selected={selected} shape="circle"><Icono nombre={NODE_META.end.icon} className="text-2xl" aria-hidden /><span className="text-xs font-medium leading-tight px-1 truncate w-full">{data.wf.name}</span></Shell>
  )),
  handoff: memo(({ id, data, selected }: NodeProps<DNode>) => (
    <Shell id={id} type="handoff" selected={selected} shape="circle"><Icono nombre={NODE_META.handoff.icon} className="text-2xl" aria-hidden /><span className="text-xs font-medium leading-tight px-1 truncate w-full" title={data.wf.name}>{data.wf.name}</span></Shell>
  )),
  human_task: memo(({ id, data, selected }: NodeProps<DNode>) => {
    const lk = useLookups(); const n = data.wf; const m = NODE_META.human_task;
    return (
      <Shell id={id} type="human_task" selected={selected}>
        <Title icon={m.icon} name={n.name} sub={nodeSubtitle(n, lk)} color={m.color} />
        {n.type === 'human_task' && (n.outcomes.length > 1 || n.requiresSignature) && (
          <div className="flex flex-wrap gap-1 mt-1.5">
            {n.outcomes.length > 1 && n.outcomes.map((o) => <span key={o} className="text-[10px] px-1.5 rounded bg-muted text-muted-foreground border border-border">{o}</span>)}
            {n.requiresSignature && <span className="text-[10px] px-1.5 rounded bg-muted text-muted-foreground border border-border inline-flex items-center gap-0.5"><Icono nombre="mdi:draw-pen" aria-hidden /> firma</span>}
          </div>
        )}
      </Shell>
    );
  }),
  system_task: memo(({ id, data, selected }: NodeProps<DNode>) => { const lk = useLookups(); const m = NODE_META.system_task; return <Shell id={id} type="system_task" selected={selected}><Title icon={m.icon} name={data.wf.name} sub={nodeSubtitle(data.wf, lk)} color={m.color} /></Shell>; }),
  decision: memo(({ id, data, selected }: NodeProps<DNode>) => (
    <Shell id={id} type="decision" selected={selected} shape="diamond"><span className="text-[12px] font-medium leading-tight inline-flex items-center gap-1"><Icono nombre={NODE_META.decision.icon} aria-hidden /> {data.wf.name}</span></Shell>
  )),
  parallel: memo(({ id, data, selected }: NodeProps<DNode>) => (
    <Shell id={id} type="parallel" selected={selected} shape="bar"><span className="text-[12px] font-medium inline-flex items-center gap-1"><Icono nombre={NODE_META.parallel.icon} aria-hidden /> {data.wf.name}</span></Shell>
  )),
  join: memo(({ id, data, selected }: NodeProps<DNode>) => { const lk = useLookups(); return (
    <Shell id={id} type="join" selected={selected} shape="bar"><span className="text-[12px] font-medium inline-flex items-center gap-1"><Icono nombre={NODE_META.join.icon} aria-hidden /> {data.wf.name}</span><span className="block text-[10px] text-muted-foreground">{nodeSubtitle(data.wf, lk)}</span></Shell>
  ); }),
  timer: memo(({ id, data, selected }: NodeProps<DNode>) => { const lk = useLookups(); return (
    <Shell id={id} type="timer" selected={selected} shape="pill"><span className="text-[12px] font-medium inline-flex items-center gap-1"><Icono nombre={NODE_META.timer.icon} aria-hidden /> {data.wf.name}</span><span className="block text-[10px] text-muted-foreground">{nodeSubtitle(data.wf, lk)}</span></Shell>
  ); }),
};
